interface ApiEnvelope<T> {
  ok: true;
  data: T;
}

interface ApiFailure {
  ok: false;
  error: { code: string; message: string; details?: Record<string, unknown> };
  requestId: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
  }
}

export interface ApiInit extends RequestInit {
  /** Gives up after this many milliseconds with a "TimeoutError" (the server may still finish the work). */
  timeoutMs?: number;
}

/** AbortSignal.timeout where it exists; a timer elsewhere (older Safari). */
export function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === "function") return AbortSignal.timeout(ms);
  const controller = new AbortController();
  setTimeout(() => controller.abort(new DOMException("The request timed out.", "TimeoutError")), ms);
  return controller.signal;
}

function requestSignal(signal: AbortSignal | null | undefined, timeoutMs: number | undefined): AbortSignal | undefined {
  if (!timeoutMs) return signal ?? undefined;
  const timeout = timeoutSignal(timeoutMs);
  if (!signal) return timeout;
  return typeof AbortSignal.any === "function" ? AbortSignal.any([signal, timeout]) : signal;
}

export async function api<T>(path: string, { timeoutMs, ...init }: ApiInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const signal = requestSignal(init.signal, timeoutMs);
  const response = await fetch(path, { ...init, headers, signal, credentials: "same-origin" });
  let body: ApiEnvelope<T> | ApiFailure;
  try {
    body = await response.json() as ApiEnvelope<T> | ApiFailure;
  } catch (error) {
    // A body cut off by the timeout (or an abort) is not a bad answer.
    if (signal?.aborted) throw signal.reason ?? error;
    throw new ApiError(response.status, "INVALID_RESPONSE", "服务器返回了无法读取的内容");
  }
  if (!response.ok || !body.ok) {
    const failure = body as ApiFailure;
    throw new ApiError(response.status, failure.error?.code ?? "REQUEST_FAILED", failure.error?.message ?? "请求失败", failure.requestId);
  }
  return body.data;
}

export function jsonBody(value: unknown): Pick<RequestInit, "body"> {
  return { body: JSON.stringify(value) };
}

export interface UploadInit {
  /** Gives up after this many milliseconds with a "TimeoutError" (the server may still finish the work). */
  timeoutMs?: number;
  /** Bytes sent so far and in all, while the body goes out. */
  onProgress?: (sent: number, total: number) => void;
}

/**
 * A JSON POST like `api`, but through XMLHttpRequest so the page can show how
 * much of a large body (a photo as base64) has gone out. Same answers and
 * errors as `api`: the envelope's data, an ApiError, or a "TimeoutError".
 */
export function apiUpload<T>(path: string, value: unknown, { timeoutMs, onProgress }: UploadInit = {}): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", path);
    request.setRequestHeader("Content-Type", "application/json");
    request.withCredentials = true;
    if (timeoutMs) request.timeout = timeoutMs;
    if (onProgress) {
      request.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(event.loaded, event.total);
      };
    }
    request.onload = () => {
      let body: ApiEnvelope<T> | ApiFailure;
      try {
        body = JSON.parse(request.responseText) as ApiEnvelope<T> | ApiFailure;
      } catch {
        reject(new ApiError(request.status, "INVALID_RESPONSE", "服务器返回了无法读取的内容"));
        return;
      }
      if (request.status < 200 || request.status >= 300 || !body?.ok) {
        const failure = body as ApiFailure;
        reject(new ApiError(request.status, failure?.error?.code ?? "REQUEST_FAILED", failure?.error?.message ?? "请求失败", failure?.requestId));
        return;
      }
      resolve(body.data);
    };
    request.ontimeout = () => reject(new DOMException("The request timed out.", "TimeoutError"));
    request.onerror = () => reject(new TypeError("Network request failed"));
    request.onabort = () => reject(new DOMException("The request was aborted.", "AbortError"));
    request.send(JSON.stringify(value));
  });
}
